import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { receiptsAPI } from '@matix/api-client';
import type { PrintConfig, ReceiptData } from '@matix/types';
import { toast } from '../../store/toast';
import { currentPrintLanguage } from './printLanguage';
import { PRINT_CONFIG_QUERY_KEY } from './usePrintConfig';
import {
  buildReceiptHtml,
  buildReprintData,
  printReceipt,
  printReportInvoice,
  usesReportInvoice,
} from '@matix/receipt';

/**
 * Print an EXISTING receipt again, by id.
 *
 * One implementation, three callers: the Receipts screen's "Print again", the
 * call-centre pending list's "Print", and its detail drawer. It used to live
 * inline in LegacyApp, which was fine while the Receipts screen was the only
 * place that reprinted — the moment a second screen needed it, a copy would
 * have meant two slips that drift apart, and a phone order handed to a customer
 * on a subtly different document from the one the till prints is exactly the
 * kind of difference nobody notices until a VAT audit.
 *
 * The print config comes out of the react-query cache rather than a fetch: it
 * is loaded once per session at the top of the shell and never refetched, so a
 * miss here means the shell has not finished booting, not that the tenant has
 * no config.
 */
/** Resolves true when the receipt was sent to the printer, false when it failed (the toast says why). */
export function useReprintReceipt(): (receiptId: number) => Promise<boolean> {
  const queryClient = useQueryClient();

  return useCallback(
    async (id: number): Promise<boolean> => {
      try {
        const printConfig = queryClient.getQueryData<PrintConfig>(PRINT_CONFIG_QUERY_KEY);
        if (!printConfig) {
          toast('Print config not loaded — please reload the page', 'error');
          return false;
        }
        // Report-server A4, when it is switched back on: it needs nothing but
        // the receipt id — no detail fetch, no local layout. Off by default; the
        // A4 sheet is laid out locally by `buildReceiptHtml` below.
        if (usesReportInvoice(printConfig)) {
          await printReportInvoice(() =>
            receiptsAPI.getInvoiceReportPdf(id, currentPrintLanguage()),
          );
          return true;
        }
        const detail = await receiptsAPI.getById(id);
        // The mapping lives in @matix/receipt so the kitchen screen prints the
        // SAME document from the same code — see buildReprintData.
        // One language for the whole slip, options included — the mapper needs
        // it too, because `mods` is flat strings by the time the renderer sees it.
        const lang = currentPrintLanguage();
        const data: ReceiptData = buildReprintData(printConfig, detail, lang);
        await printReceipt(buildReceiptHtml({ ...data, language: lang }));
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to reprint';
        toast(msg, 'error');
        return false;
      }
    },
    [queryClient],
  );
}
